/**
 * 인벤토리 관련 서비스
 */
import carbonApi from "../api";
import { unwrapResponse, extractArrayData } from "../utils/api.js";

/**
 * 인벤토리 목록 조회
 * @param {Object} params - 조회 파라미터
 * @returns {Promise<Object>} 인벤토리 목록 및 페이지네이션 정보
 */
export async function fetchInventories(params = {}) {
    const response = await carbonApi.inventory.getInventories(params);
    const data = unwrapResponse(response, { defaultMessage: "인벤토리 목록을 불러오지 못했습니다." });

    if (Array.isArray(data?.data)) {
        return {
            items: data.data,
            pagination: data.pagination || null,
        };
    }

    return {
        items: extractArrayData(data, ["inventories", "items", "data"]),
        pagination: data?.pagination || null,
    };
}

/**
 * 스텝별 인벤토리 목록 조회 (GET /workflow-steps/:stepIdx/inventories)
 * @param {number} stepIdx - 워크플로우 스텝 인덱스
 * @returns {Promise<Object>} { items: 인벤토리 배열 }
 */
export async function fetchInventoriesByStep(stepIdx) {
    if (stepIdx == null) {
        return { items: [] };
    }
    const response = await carbonApi.inventory.getInventoriesByStep(Number(stepIdx));
    const unwrapped = unwrapResponse(response, { defaultMessage: "스텝 인벤토리 목록을 불러오지 못했습니다." });
    // API: { success, data: { stepIdx, period, orgIdx, data: [...], totalCount } } → unwrapResponse는 data만 반환 → unwrapped = { stepIdx, period, orgIdx, data: [...], totalCount }
    const items = Array.isArray(unwrapped?.data) ? unwrapped.data : [];
    return { items };
}

/**
 * 인벤토리 상세 조회
 * @param {string|number} inventoryIdx - 인벤토리 인덱스
 * @returns {Promise<Object>} 인벤토리 정보
 */
export async function fetchInventoryDetail(inventoryIdx) {
    if (!inventoryIdx) {
        throw new Error("inventoryIdx는 필수입니다.");
    }

    const response = await carbonApi.inventory.getInventory(inventoryIdx);
    return unwrapResponse(response, { defaultMessage: "인벤토리 정보를 불러오지 못했습니다." });
}

/**
 * 인벤토리 라인 조회 결과 정규화
 * - 월/연 라인 API의 필드 차이를 UI에서 숨기기 위한 공통 shape
 */
function normalizeInventoryLine(mode, raw) {
    if (!raw || typeof raw !== "object") return null;

    const isAnnual = mode === "annual";
    const timeKey = isAnnual ? raw.year : raw.period;
    const inventoryKey = isAnnual ? raw.annualInventoryIdx : raw.inventoryIdx;
    const co2eqTotal = isAnnual ? raw.CO2eq ?? raw.CO2eqTotal : raw.CO2eqTotal ?? raw.CO2eq;

    return {
        // 공통 키
        mode,
        timeKey, // 월: YYYYMM, 연: YYYY
        inventoryKey, // 월: inventoryIdx, 연: annualInventoryIdx

        // 원본 식별/축
        lineIdx: raw.lineIdx,
        inventoryIdx: raw.inventoryIdx ?? null,
        annualInventoryIdx: raw.annualInventoryIdx ?? null,
        period: raw.period ?? null,
        year: raw.year ?? null,

        orgBoundaryIdx: raw.orgBoundaryIdx ?? null,
        operBoundaryIdx: raw.operBoundaryIdx ?? null,
        facilityCode: raw.facilityCode ?? null,
        categoryCode: raw.categoryCode ?? null,
        scope: raw.scope ?? null,
        activityCount: raw.activityCount ?? null,

        // 값
        CO2: raw.CO2,
        CH4: raw.CH4,
        N2O: raw.N2O,
        HFCs: raw.HFCs,
        PFCs: raw.PFCs,
        SF6: raw.SF6,

        CO2eqCO2: raw.CO2eqCO2,
        CO2eqCH4: raw.CO2eqCH4,
        CO2eqN2O: raw.CO2eqN2O,
        CO2eqHFCs: raw.CO2eqHFCs,
        CO2eqPFCs: raw.CO2eqPFCs,
        CO2eqSF6: raw.CO2eqSF6,

        co2eqTotal,

        // 상태/워크플로우
        inventoryStat: raw.inventoryStat ?? null,
        runIdx: raw.runIdx ?? null,
        stepIdx: raw.stepIdx ?? null,
    };
}

export async function fetchInventoryLines(params = {}) {
    const response = await carbonApi.inventory.getInventoryLines(params);
    const result = unwrapResponse(response, { defaultMessage: "인벤토리 라인 목록을 불러오지 못했습니다." });
    const lines = Array.isArray(result?.lines) ? result.lines : Array.isArray(result) ? result : [];
    const items = lines.map((l) => normalizeInventoryLine("monthly", l)).filter(Boolean);
    return {
        items,
        count: result?.count ?? items.length,
        appliedFilters: result?.appliedFilters ?? null,
    };
}

export async function fetchAnnualInventoryLines(params = {}) {
    const response = await carbonApi.inventory.getAnnualInventoryLines(params);
    const result = unwrapResponse(response, { defaultMessage: "연간 인벤토리 라인 목록을 불러오지 못했습니다." });
    const lines = Array.isArray(result?.lines) ? result.lines : Array.isArray(result) ? result : [];
    const items = lines.map((l) => normalizeInventoryLine("annual", l)).filter(Boolean);
    return {
        items,
        count: result?.count ?? items.length,
        appliedFilters: result?.appliedFilters ?? null,
    };
}

export async function fetchAnnualInventoryDetail(annualInventoryIdx) {
    if (!annualInventoryIdx) {
        throw new Error("annualInventoryIdx는 필수입니다.");
    }
    const response = await carbonApi.inventory.getAnnualInventory(annualInventoryIdx);
    return unwrapResponse(response, { defaultMessage: "연간 인벤토리 정보를 불러오지 못했습니다." });
}

/**
 * 인벤토리 생성 (서버: POST /plugins/carbon/calculations/runs/with-boundaries)
 * @param {Object} payload - 인벤토리 생성 페이로드
 * @param {number} payload.orgBoundaryIdx - 조직 경계 ID (필수)
 * @param {number} payload.operBoundaryIdx - 운영 경계 ID (필수)
 * @param {string} payload.period - 기준 월 YYYYMM (필수, 단일)
 * @param {boolean} [payload.recalculate] - 재계산 여부 (선택, 기본 false)
 * @returns {Promise<Object>} 생성된 인벤토리 정보
 */
export async function createInventory(payload) {
    const response = await carbonApi.inventory.createInventory(payload);
    const result = unwrapResponse(response, { defaultMessage: "인벤토리 생성에 실패했습니다.", includeMessage: true });

    return {
        success: result.success ?? true,
        message: result.message,
        inventory: result.data || result,
    };
}

/**
 * 인벤토리 삭제
 * @param {string|number} inventoryIdx - 인벤토리 인덱스
 * @returns {Promise<Object>} 삭제 결과
 */
export async function deleteInventory(inventoryIdx) {
    if (!inventoryIdx) {
        throw new Error("inventoryIdx는 필수입니다.");
    }

    const response = await carbonApi.inventory.deleteInventory(inventoryIdx);
    const data = unwrapResponse(response, { defaultMessage: "인벤토리 삭제에 실패했습니다.", includeMessage: true });

    return {
        success: data?.success ?? true,
        message: data?.message || "인벤토리가 삭제되었습니다.",
    };
}

/**
 * 인벤토리 추이 조회
 * @param {Object} params - 조회 파라미터
 * @returns {Promise<Array>} 인벤토리 추이 데이터
 */
export async function fetchInventoryTrends(params) {
    const response = await carbonApi.inventory.getInventoryTrends(params);
    const result = unwrapResponse(response, { defaultMessage: "인벤토리 추이를 불러오지 못했습니다." });
    return result?.trends || result || [];
}

/**
 * 인벤토리 비교
 * @param {Object} payload - 비교 파라미터
 * @returns {Promise<Object>} 비교 결과
 */
export async function compareInventories(payload) {
    const response = await carbonApi.inventory.compareInventories(payload.inventory1, payload.inventory2);
    const result = unwrapResponse(response, { defaultMessage: "인벤토리 비교에 실패했습니다." });
    return result?.data || result;
}

/**
 * 인벤토리 품질 점검
 * @param {string|number} inventoryIdx - 인벤토리 인덱스
 * @returns {Promise<Object>} 품질 점검 결과
 */
export async function fetchQualityReport(inventoryIdx) {
    const response = await carbonApi.inventory.checkInventoryQuality(inventoryIdx);
    return unwrapResponse(response, { defaultMessage: "품질 점검 결과를 불러오지 못했습니다." });
}

/**
 * 인벤토리 불확실성 평가
 * @param {string|number} inventoryIdx - 인벤토리 인덱스
 * @returns {Promise<Object>} 불확실성 평가 결과
 */
export async function fetchUncertainty(inventoryIdx) {
    const response = await carbonApi.inventory.assessUncertainty(inventoryIdx);
    return unwrapResponse(response, { defaultMessage: "불확실성 평가 결과를 불러오지 못했습니다." });
}

/**
 * 기준 배출량 조회
 * @param {Object} params - 조회 파라미터 (orgIdx, boundaryIdx, operBoundaryIdx, baseYear)
 * @returns {Promise<Object>} 기준 배출량 정보
 */
export async function fetchBaselineEmission({ orgIdx, boundaryIdx, operBoundaryIdx, baseYear }) {
    if (!orgIdx || !baseYear) {
        throw new Error("orgIdx와 baseYear는 필수입니다.");
    }

    const params = {
        orgIdx,
        boundaryIdx,
        operBoundaryIdx,
        startYear: baseYear,
        endYear: baseYear,
        limit: 1,
        page: 1,
    };

    const data = await fetchInventories(params);
    const inventories = data.items || [];
    const record = Array.isArray(inventories) ? inventories[0] : inventories;

    return {
        emission: record?.totalEmission ?? record?.totalEmissions ?? 0,
        inventory: record || null,
    };
}

/**
 * 경계 추이 조회
 * @param {Object} params - 조회 파라미터
 * @returns {Promise<Object>} 추이 데이터
 */
export async function fetchBoundaryTrends({ boundaryIdx, operBoundaryIdx, periodFrom, periodTo, groupBy = "year" }) {
    if (!boundaryIdx || !operBoundaryIdx) {
        throw new Error("boundaryIdx와 operBoundaryIdx는 필수입니다.");
    }

    const response = await carbonApi.inventory.getInventoryTrends({
        boundaryIdx,
        operBoundaryIdx,
        periodFrom,
        periodTo,
        groupBy,
    });

    return unwrapResponse(response, { defaultMessage: "배출 추이를 불러오지 못했습니다." });
}
